import React, { useState, useCallback, useEffect, useRef } from 'react';
import { XIcon } from 'lucide-react';
import { useQuote } from '../../context/QuoteContext';
import type {
    Vehicle,
    VehicleDerivative,
    AdditionalVehicleDetailsFormInputs,
} from '@/features/vehiclesearch';
import {
    AdditionalVehicleDetailsForm,
    VehicleSearch,
    getVehicleDisplayInfo,
} from '@features/vehiclesearch';
import { Alert, PageHeader, Button, Tooltip } from '@/shared/components';
import { MotHistoryPanel } from '@/features/mot-history';
import type { MotMetrics } from '@/features/mot-history';
import { useDealerConfig } from '@/features/dealers/hooks/useDealerConfig';
import { usePermissions } from '@/features/auth';
import { useExistingAgreementCheck } from '../../hooks/useExistingAgreementCheck';
import { useSeenAgreementCheck } from '../../hooks/useSeenAgreementCheck';
import { ExistingAgreementNotice, LookupAgreementNotice } from './ExistingAgreementNotice';

/**
 * #819. The stored vehicle on a resumed quote can be a shape this form cannot repair.
 * Two of the three known shapes reach this component's catch block below and render the
 * alert; the third does not, and is disclosed separately after them.
 *
 *   S1 — type: 'vehicle' with current_status but no full_description or no derivative
 *        (the QuoteFactory.php:59-79 shape). updateExistingVehicle spreads the document,
 *        so the gaps survive, and validateVehicleData (QuoteContext.tsx:242-250) throws.
 *   S2 — type: 'vehicle' with no current_status key at all (a VIN-only CarWeb lookup —
 *        VehicleSearchResource.php:149 / vinvrmsearch.ts:198-200). isVehicle() is then
 *        false, the document falls into convertDerivativeToVehicle, and
 *        derivative.make.name raises a TypeError.
 *   S3 — CLOSED by #859 (docs/plans/2026-08-10-vehicle-details-form-silent-validity-failure.md).
 *        A stored vin/vrm that fails additionalvehicledetails.schema.ts's length bounds
 *        used to hold AdditionalVehicleDetailsForm's whole-schema `isValid` false forever
 *        with no field error and no alert -- the user picked a date and watched nothing
 *        happen. #859 corrected the bounds (vin 11-17, vrm 2-7), normalises the vin/vrm
 *        seed so a merely misformatted stored value repairs itself silently, and
 *        generalised #807's value-driven message to every field via a `fieldIssues`
 *        derivation in additionalVehicleDetailsFormState.ts, so a genuinely out-of-range value
 *        now renders a message at rest instead of nothing. Still not this step's concern:
 *        the fix lives entirely in the form, not here.
 *
 * Both S1 and S2's throws used to be swallowed: this handler awaited and console.log'd,
 * and the form's auto-submit effect caught and console.error'd
 * (useVehicleDetailsAutoSubmit.ts). Nothing reached the screen. Tolerable
 * while nothing sent users here; NOT tolerable once #819's routing sends them to this
 * step, because they would fill in the date and watch nothing happen.
 *
 * Catching here rather than in the form is deliberate: the step owns the user-facing
 * outcome, and swallowing the rejection also stops the form re-firing the same failing
 * submit on every subsequent render (it sets lastSubmittedData, :236).
 *
 * THE COPY BELOW DELIBERATELY DOES NOT PROMISE A WORKING RECOVERY. Two loops were found
 * here and closed; do not "tidy" the copy back to something shorter that reopens either:
 *
 *   1. "Search for it again by registration number" does not recover S2.
 *      VehicleSearchResource.php:149 emits current_status: null whenever there is no
 *      VRM and vinvrmsearch.ts:198-200 / :220-222 copy the key only when truthy, so
 *      re-searching the same VIN reproduces the identical shape and this alert
 *      re-appears. Copy that said "search for it again to continue" would send that
 *      user round a loop -- exactly the "polite but unrecoverable" failure D1 rejects.
 *      Naming the route as conditional on the vehicle being findable by registration
 *      number -- not as a guaranteed fix -- is the honest version.
 *   2. "Start a new quote for this vehicle" does not recover S2 either, and an earlier
 *      revision of this copy said it did. A fresh quote built from the same VIN lookup
 *      produces the identical current_status-less document -- the same two citations
 *      as above -- so that instruction was a second, disguised loop dressed up as an
 *      exit. D3 of docs/plans/2026-08-07-quote-resume-null-registration-date.md names
 *      the genuinely terminal action and this copy now uses it: mark the quote dead
 *      (PATCH /quotes/{id}/mark-dead) and escalate, not self-serve a replacement quote.
 */
const VEHICLE_DETAILS_NOT_APPLIED =
    'We could not update this vehicle. The saved vehicle record is incomplete, so the ' +
    'registration date could not be applied. If the vehicle can be found by registration ' +
    'number, clear the selected vehicle above and search for it again that way — this ' +
    'repairs the record. If it cannot be found that way, this quote cannot be repaired ' +
    'here. Do not start a new quote for the same vehicle: the same search reproduces this ' +
    'error. Escalate instead, so the quote can be marked dead.';

const renderLookupNotice = (vehicle: Vehicle) => <LookupAgreementNotice vehicle={vehicle} />;

export const VehicleSelectionStep: React.FC = () => {
    const { selectedVehicle, selectVehicle, updateVehicleWithDetails, reportVehicleDetailsBlocked } =
        useQuote();

    // Resolves for the selected dealer, falling back to the user's home dealer.
    // Defaults false until the config arrives, so the flag may flip after first render.
    const { prePopulateMileage } = useDealerConfig();
    const [motSeed, setMotSeed] = useState<{ key: string; metrics: MotMetrics } | null>(null);
    const [isMotLoading, setIsMotLoading] = useState(false);
    const [detailsError, setDetailsError] = useState<string | null>(null);
    const headingRef = useRef<HTMLHeadingElement>(null);

    // Recovery from NoSellingDealerError can swap this step in and unmount the focused button,
    // which would drop focus to <body>.
    useEffect(() => {
        headingRef.current?.focus();
    }, []);

    const existingAgreementCheck = useExistingAgreementCheck(selectedVehicle);
    const { recordSelection, isSeen } = useSeenAgreementCheck(selectedVehicle);
    const { hasPermission } = usePermissions();
    const canViewAgreement = hasPermission({ resource: 'agreement', action: 'read' });

    const motVin =
        selectedVehicle?.type === 'vehicle' ? selectedVehicle.vin ?? undefined : undefined;
    const motVrm =
        selectedVehicle?.type === 'vehicle'
            ? selectedVehicle.current_status?.vrm ?? undefined
            : undefined;

    // Identity of the vehicle the MOT lookup is keyed to this render.
    const motKey = `${motVin ?? ''}|${motVrm ?? ''}`;

    // Stamp incoming metrics with the key they belong to. Because motKey is a
    // dependency, onMetrics' identity tracks the current vehicle, so the child
    // panel re-reports against the new key when the selection changes.
    const handleMetrics = useCallback(
        (m: MotMetrics) => setMotSeed({ key: motKey, metrics: m }),
        [motKey],
    );

    // Only apply a seed that belongs to the currently selected vehicle. A seed
    // tagged to a previous vehicle is ignored (effectiveSeed === null) until that
    // vehicle's own MOT metrics arrive — preserving the stale-seed safety guarantee
    // the deleted clear effect used to provide, without an order-dependent write.
    const effectiveSeed = motSeed?.key === motKey ? motSeed.metrics : null;

    // Invalidate the seed whenever the vehicle is deselected. The key match alone
    // protects cross-vehicle switches, but clearing the selection and re-selecting
    // the SAME vehicle would otherwise replay the prior seed from local state
    // before the fresh MOT lookup returns (and keep showing it if that lookup
    // fails). This is free of the original clobber race: when no vehicle is
    // selected the MotHistoryPanel is unmounted and cannot write motSeed, so this
    // is the only writer in that commit. Re-selecting then re-derives the seed
    // from the vehicle's own (cache or network) lookup.
    useEffect(() => {
        if (!selectedVehicle) {
            setMotSeed(null);
            setDetailsError(null); // #819 — Clear Selected Vehicle is the stated recovery
        }
    }, [selectedVehicle]);

    const handleVehicleSelect = (vehicle: Vehicle | VehicleDerivative) => {
        recordSelection(vehicle);
        selectVehicle(vehicle);
    };

    const handleAdditionalVehicleDetailsSubmit = async (
        data: AdditionalVehicleDetailsFormInputs
    ) => {
        try {
            await updateVehicleWithDetails(data);
            setDetailsError(null);
        } catch (error) {
            console.error('Failed to apply vehicle details:', error);
            setDetailsError(VEHICLE_DETAILS_NOT_APPLIED);
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap justify-between content-start items-start">
                <PageHeader
                    title={selectedVehicle ? 'Additional Vehicle Details' : 'Select Vehicle'}
                    subtitle={
                        selectedVehicle
                            ? 'Please confirm the following additional vehicle details.'
                            : `Please search for and select the customer's vehicle that will be covered by the service plan.`
                    }
                    headingRef={headingRef}
                    headingTabIndex={-1}
                />
            </div>
            {selectedVehicle ? (
                <>
                    <Alert variant="success" role="status">
                        <div className="grow">
                            {(() => {
                                const displayInfo = getVehicleDisplayInfo(selectedVehicle);
                                return (
                                    <>
                                        <p className="font-medium">
                                            Selected: {displayInfo.year} {displayInfo.make}{' '}
                                            {displayInfo.model} ({displayInfo.variant})
                                        </p>
                                        {selectedVehicle.type === 'vehicle' && (
                                            <p>
                                                VIN: {displayInfo.vin} | Registration:{' '}
                                                {displayInfo.registration}
                                            </p>
                                        )}
                                    </>
                                );
                            })()}
                        </div>
                        {/*
                         * #819. `ariaLabel` here is load-bearing, not decoration. The
                         * recovery copy below names this control ("clear the selected
                         * vehicle above") as the first step out of an unrepairable
                         * document, and the control is icon-only — `startIcon` with no
                         * children — so without an explicit name a screen reader
                         * announces an unlabelled button and the documented recovery is
                         * unreachable. The `Tooltip` does NOT supply the name: its
                         * sr-only text (Tooltip.tsx:53) is a SIBLING of the button
                         * inside the trigger span rather than content of it, and its
                         * bubble is `role="tooltip"` with no `aria-labelledby` or
                         * `aria-describedby` wired to the child. Button.tsx:14-21
                         * documents `ariaLabel` as the contract for exactly this case.
                         * The value matches the tooltip text verbatim so the spoken
                         * name and the visible hint agree (WCAG 2.5.3, Label in Name).
                         */}
                        <div className="justify-self-end">
                            <Tooltip content="Clear Selected Vehicle">
                                <Button
                                    onClick={() => selectVehicle(null)}
                                    className="text-mp-success pt-1"
                                    variant="clear"
                                    size="none"
                                    ariaLabel="Clear Selected Vehicle"
                                    startIcon={<XIcon size={22} />}
                                />
                            </Tooltip>
                        </div>
                    </Alert>
                    {!isSeen && (
                        <ExistingAgreementNotice
                            checkState={existingAgreementCheck}
                            canViewAgreement={canViewAgreement}
                        />
                    )}
                    {detailsError && (
                        // role="alert" IS correct here, unlike the resting required message from
                        // additionalVehicleDetailsFormState.ts's deriveIssues. This node appears only in response to a
                        // user action that failed, never on mount, so there is no pristine-state
                        // announcement to suppress — the same distinction #823 drew and the same reason
                        // CollectionLeadInput.tsx:225-229 sets it unconditionally on a node that only
                        // exists once there is an error.
                        <Alert variant="error">{detailsError}</Alert>
                    )}
                    <AdditionalVehicleDetailsForm
                        onSubmit={handleAdditionalVehicleDetailsSubmit}
                        selectedVehicle={selectedVehicle}
                        autoSubmit={true}
                        seededDetails={effectiveSeed}
                        isMotLoading={isMotLoading}
                        prePopulateMileage={prePopulateMileage}
                        // #825 — the form half of the vehicle step gate (D2/D3/D10).
                        onRegistrationDateBlockedChange={reportVehicleDetailsBlocked}
                    />
                    <MotHistoryPanel
                        vin={motVin}
                        vrm={motVrm}
                        showHeader
                        onMetrics={handleMetrics}
                        onLoadingChange={setIsMotLoading}
                    />
                </>
            ) : (
                <>
                    <VehicleSearch
                        onSelectVehicle={handleVehicleSelect}
                        selectedVehicle={selectedVehicle}
                        renderLookupNotice={renderLookupNotice}
                    />

                    <Alert variant="warning" role="note">
                        Please select a vehicle to continue to the next step.
                    </Alert>
                </>
            )}
        </div>
    );
};
